import {forwardRef, useRef, useState} from 'react'
import {ComboboxInput, ComboboxItem} from '@/components/ui/combobox-atoms/ComboboxAtoms'
import {Divider} from '@/components/ui/divider/Divider'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {PlusIcon, SearchSmIcon} from '@/components/ui/icon/Icon'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {Label} from '@/components/ui/label/Label'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {Text} from '@/components/ui/text/Text'
import {useInputSearch} from '@/hooks/useInputSearch'
import {useTranslation} from '@/translations/i18n'
import {ComboboxProvider, useComboboxStore} from '@ariakit/react'
import {
    StyledAddSpecialistButton,
    StyledSearchSpecialistPopover,
    StyledSelectSpecialistList,
    StyledSpecialistComboboxItem
} from '@/features/projects/specialists/components/input-search-specialist/style'
import {Badge} from '@/components/ui/badge/Badge'
import {useSpecialistsList} from '@/features/projects/specialists/hooks/useSpecialistsList'
import {CreateUpdateReferringSpecialist} from '@/features/referring-specialists/components/create-update-referring-specialist/CreateUpdateReferringSpecialist'
import {ReferringSpecialist} from '@/features/referring-specialists/types'
import {usePermissions} from '@/features/auth/store/store'
import {specialistTitleMapToLabel} from '@/features/referring-specialists/utils'

interface InputSearchSpecialistProps {
    errorMessage?: string
    placeholder?: string
    label?: string
    defaultValue?: ReferringSpecialist
    onChange: (value: ReferringSpecialist) => void
    onCreateSpecialist?: (specialist: ReferringSpecialist) => void
    hideAddSpecialistButton?: boolean
}

export const InputSearchSpecialist = forwardRef<HTMLInputElement, InputSearchSpecialistProps>(
    ({errorMessage, placeholder, defaultValue, label, onChange, onCreateSpecialist, hideAddSpecialistButton}, ref) => {
        const {t} = useTranslation()
        const [open, setOpen] = useState(false)
        const {searchValue, onSearch} = useInputSearch()
        const comboboxInputRef = useRef(null)

        const comboboxStore = useComboboxStore({
            defaultValue: defaultValue?.id,
            setValue: onSearch,
            open,
            setOpen
        })

        const {specialists, isFetching, isError} = useSpecialistsList({
            search: searchValue,
            enabled: open
        })

        const handleSelect = (value: string) => {
            const specialistFound = specialists?.find(specialist => specialist.id == value)
            specialistFound && onChange(specialistFound)
            setOpen(false)
        }

        const handleCreateSpecialist = (specialist: ReferringSpecialist) => {
            if (specialist && onCreateSpecialist) {
                onCreateSpecialist(specialist)
                setOpen(false)
            }
        }

        const permissions = usePermissions()

        return (
            <>
                <ComboboxProvider store={comboboxStore} placement="top-start">
                    <Flexbox direction="column" gap={1.5} fullWidth>
                        {label && <Label>{label}</Label>}
                        <ComboboxInput
                            hasCloseCombobox
                            typeIcon={<SearchSmIcon size={16} />}
                            errorMessage={errorMessage}
                            placeholder={placeholder}
                            ref={ref || comboboxInputRef}
                        />
                        {!!errorMessage && (
                            <Flexbox align="center" gap={1.5}>
                                <InputStatusIcon hasError={true} />
                                <InputHelpText error={errorMessage} />
                            </Flexbox>
                        )}
                        <StyledSearchSpecialistPopover flip={false}>
                            <StyledSelectSpecialistList>
                                {isFetching ? (
                                    <Flexbox direction="column" gap={2} fullWidth>
                                        {Array.from({length: 6}, (_, index) => (
                                            <Skeleton width="100%" key={index} height={32} />
                                        ))}
                                    </Flexbox>
                                ) : isError ? (
                                    <ComboboxItem disabled>
                                        <InputHelpText error={t('errors:default')} />
                                    </ComboboxItem>
                                ) : specialists && specialists.length > 0 ? (
                                    specialists.map(specialist => (
                                        <StyledSpecialistComboboxItem
                                            key={specialist.id}
                                            id={specialist.id}
                                            onClick={() => handleSelect(specialist.id)}
                                            value={`${specialist.firstName} ${specialist.lastName}`}
                                        >
                                            <Flexbox align="center" justify="space-between" fullWidth>
                                                <Flexbox direction="column" gap={1}>
                                                    <Text
                                                        render="span"
                                                        weight="500"
                                                        size="textSm"
                                                    >{`${specialist.title && t(specialistTitleMapToLabel[specialist.title]) + ' '}${specialist.firstName} ${specialist.lastName}`}</Text>
                                                    <Text palette="neutral" shade="500" size="textXs">
                                                        {specialist.email}
                                                    </Text>
                                                </Flexbox>
                                                <Badge shape="rectangle">
                                                    {/* {t(projectServiceUocToLabel[specialist.serviceUoc])} */}
                                                    {specialist.serviceUoc}
                                                </Badge>
                                            </Flexbox>
                                        </StyledSpecialistComboboxItem>
                                    ))
                                ) : (
                                    <Text palette="neutral" shade="500" size="textSm">
                                        {t('commons:no_results')}
                                    </Text>
                                )}
                            </StyledSelectSpecialistList>
                            {!hideAddSpecialistButton && permissions?.settings.write && (
                                <>
                                    <Divider direction="horizontal" />
                                    <CreateUpdateReferringSpecialist
                                        onMutateSpecialistSuccess={handleCreateSpecialist}
                                        trigger={
                                            <StyledAddSpecialistButton variant="ghost" shape="text">
                                                <PlusIcon />
                                                {t('projects:add_specialist')}
                                            </StyledAddSpecialistButton>
                                        }
                                    />
                                </>
                            )}
                        </StyledSearchSpecialistPopover>
                    </Flexbox>
                </ComboboxProvider>
            </>
        )
    }
)
